import type { DeepMapTo } from 'venn-utils';
import { type Theme } from 'venn-ui-kit';
import { cva } from 'class-variance-authority';
import React from 'react';

export const TOP_TOOLBAR_HEIGHT = '65px';

type ThemeProvider = (props: { theme: Theme }) => string;

export interface TopBarColor {
  DarkGrey: string;
  MidGrey2: string;
  Background: string;
  ActiveBackground: string;
  Border: string;
}

export type GetTopBarColorType = DeepMapTo<TopBarColor, ThemeProvider>;

export const getTopBarColor = ({
  isReport,
  isTemplate,
}: { isReport: boolean; isTemplate: true } | { isReport?: boolean; isTemplate: false }): GetTopBarColorType => {
  if (isTemplate) {
    return {
      DarkGrey: () => '#0000004D', // black, 30% opacity
      MidGrey2: ({ theme }) => theme.Colors.GreyScale.Grey60,
      Background: () => (isReport ? '#225457' : '#544B14'),
      ActiveBackground: ({ theme }) => (isReport ? theme.Colors.DEPRECATED_DivergingColor.B4 : '#7C7026'),
      Border: ({ theme }) => theme.Colors.GreyScale.Grey30,
    };
  }
  return {
    DarkGrey: ({ theme }) => theme.Colors.GreyScale.Grey90,
    MidGrey2: ({ theme }) => theme.Colors.GreyScale.Grey60,
    Background: ({ theme }) => theme.Colors.GreyScale.Grey90,
    ActiveBackground: ({ theme }) => theme.Colors.GreyScale.Grey70,
    Border: ({ theme }) => theme.Colors.GreyScale.Grey30,
  };
};

export const topBarButton = cva('h-full', {
  variants: {
    active: {
      true: 'bg-white text-venn-dark-blue font-bold hover:text-venn-dark-blue hover:bg-venn-grey-20 [&>div]:border-r-transparent',
      false: 'bg-none text-white hover:text-white hover:bg-venn-grey-80',
    },
  },
  defaultVariants: { active: false },
});

/**
 * A hidden label with bold text is rendered alongside the regular visible one to prevent
 * width of the menu shifting on activation of a button - the width is calculated based on
 * the hidden bold text, so the width is always the same, regardless of active state.
 *
 * @param children
 * @constructor
 */
export const TopBarButtonLabel = ({ children }: { children: React.ReactNode }) => (
  <>
    <div className="flex items-center gap-[3px] px-[18px] h-[18px] border-0 border-r group-aria-expanded:border-r-transparent border-solid border-venn-grey-60">
      {children}
    </div>
    <div
      aria-hidden="true"
      className="flex items-center gap-[3px] px-[18px] h-[18px] border-0 border-r border-solid border-venn-grey-60 -mt-[18px] invisible font-bold"
    >
      {children}
    </div>
  </>
);
